import { useState } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Handshake, Check, Copy, Gift, QrCode, Zap, Loader2, ArrowRight, Sparkles,
} from "lucide-react";

const CITIES = ["Corvallis", "Monmouth/Independence", "Eugene", "Salem", "Other"];
const PARTNER_TYPES = ["Student/Greek", "Venue/Bar", "Restaurant/Cafe", "Indie Artist", "Creator", "Club/Other"];
const INTERESTS = ["Referral Program", "Host an Event", "Venue Promotion", "Content Collab", "Campus Ambassador", "Flyers / QR Cards"];
const REACH = ["< 500", "500 – 2k", "2k – 10k", "10k – 50k", "50k+"];
const PAYOUTS = ["Venmo", "Cash App", "Apple Pay", "PayPal", "Check", "Stripe", "Other"];
const PAYOUT_PLACEHOLDERS = {
  "Cash App": "Enter your $Cashtag",
  "Venmo": "Enter your @Venmo-handle",
  "Apple Pay": "Enter mobile number for Apple Cash",
  "Check": "Enter legal mailing address",
  "PayPal": "Enter PayPal email address",
  "Stripe": "Enter your Stripe account email",
  "Other": "Enter details or preferred method",
};

const INK = "#121214";
const EMBER = "#FF5E3A";
const MAGENTA = "#D83A99";
const field = "bg-[#1A1A1E] border-white/[0.12] focus:border-[#FF5E3A] focus-visible:ring-[#FF5E3A]/30 text-white placeholder:text-slate-500 rounded-xl";

const Pill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
      active
        ? "text-white border-transparent"
        : "text-slate-300 border-white/[0.12] bg-[#1A1A1E] hover:border-white/30"
    }`}
    style={active ? { background: `linear-gradient(135deg, ${EMBER}, ${MAGENTA})` } : {}}
  >
    {children}
  </button>
);

const MILESTONES = [
  { icon: Gift, tier: "Tier 1", reward: "Digital Partner Kit", when: "On approval" },
  { icon: QrCode, tier: "Tier 2", reward: "50 Printed QR Cards", when: "At 10 signups" },
  { icon: Zap, tier: "Tier 3", reward: "NFC Tap Card", when: "At 25 signups" },
];

export default function Partnerships() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", city_region: "", partner_type: "",
    organization_name: "", online_handle_url: "", influence_reach: "",
    partnership_interests: [], pitch_vision: "", payout_preference: "", custom_payout_details: "", address: "", zip: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleInterest = (i) =>
    setF((s) => ({
      ...s,
      partnership_interests: s.partnership_interests.includes(i)
        ? s.partnership_interests.filter((x) => x !== i)
        : [...s.partnership_interests, i],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.email || !f.phone || !f.city_region || !f.partner_type || !f.influence_reach) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/partners", f);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit application.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(result.referral_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const qrSrc = result
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=10&bgcolor=1A1A1E&color=FFFFFF&data=${encodeURIComponent(result.referral_link)}`
    : "";

  return (
    <PublicLayout>
      <section className="min-h-[70vh]" style={{ backgroundColor: INK }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
          {/* Hero */}
          <div className="text-center mb-10">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.1] text-xs uppercase font-semibold tracking-[0.2em]"
              style={{ color: EMBER, backgroundColor: "#1A1A1E" }}
            >
              <Handshake className="w-3.5 h-3.5" /> Partner Program
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
              Grow Sparkd{" "}
              <span style={{ background: `linear-gradient(135deg, ${EMBER}, ${MAGENTA})`, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                with us
              </span>
            </h1>
            <p className="mt-4 text-slate-400">
              Venues, creators, Greek life &amp; local legends — earn rewards for every member you bring in.
            </p>
          </div>

          {result ? (
            /* ---------- SUCCESS STATE ---------- */
            <div className="rounded-2xl border border-white/[0.08] p-8 text-center" style={{ backgroundColor: "#1A1A1E" }} data-testid="partner-success">
              <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-2xl" style={{ background: `linear-gradient(135deg, ${EMBER}, ${MAGENTA})` }}>
                <Check className="w-7 h-7 text-white" strokeWidth={3} />
              </div>
              <h2 className="mt-5 font-heading text-2xl font-bold text-white">You're in — welcome, partner!</h2>
              <p className="mt-2 text-sm text-slate-400">Application received{result.ticket_ref ? ` (ref #${result.ticket_ref})` : ""} — our partnerships team has been notified and will email you next steps. Share your link below.</p>

              <div className="mt-6 rounded-xl border border-white/[0.1] bg-[#121214] p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Your referral code</p>
                <p className="font-mono text-lg font-bold" style={{ color: EMBER }} data-testid="partner-code">{result.referral_code}</p>
                <div className="mt-3 flex items-center gap-2">
                  <Input readOnly value={result.referral_link} className={field} data-testid="partner-link" />
                  <Button type="button" onClick={copyLink} data-testid="partner-copy" className="rounded-xl shrink-0 text-white" style={{ background: `linear-gradient(135deg, ${EMBER}, ${MAGENTA})` }}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="mt-6 inline-block rounded-xl border border-white/[0.1] bg-[#121214] p-4">
                <img src={qrSrc} alt="Referral QR code" width={200} height={200} className="rounded-lg" data-testid="partner-qr" />
                <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-slate-400"><QrCode className="w-3.5 h-3.5" /> Scan to share</p>
              </div>

              <div className="mt-8 grid sm:grid-cols-3 gap-3 text-left">
                {MILESTONES.map((m) => (
                  <div key={m.tier} className="rounded-xl border border-white/[0.08] bg-[#121214] p-4" data-testid="milestone-badge">
                    <m.icon className="w-5 h-5" style={{ color: MAGENTA }} />
                    <p className="mt-2 text-xs font-bold uppercase tracking-wide" style={{ color: EMBER }}>{m.tier}</p>
                    <p className="text-sm font-semibold text-white">{m.reward}</p>
                    <p className="text-xs text-slate-500">{m.when}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* ---------- INTAKE FORM ---------- */
            <form onSubmit={submit} className="rounded-2xl border border-white/[0.08] p-6 sm:p-8 space-y-6" style={{ backgroundColor: "#1A1A1E" }} data-testid="partner-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Full name *</Label>
                  <Input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Doe" className={field} data-testid="pf-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email *</Label>
                  <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className={field} data-testid="pf-email" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone *</Label>
                  <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" className={field} data-testid="pf-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Organization / Handle</Label>
                  <Input value={f.organization_name} onChange={(e) => set("organization_name", e.target.value)} placeholder="Business or @handle" className={field} data-testid="pf-org" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">City / Region *</Label>
                <div className="flex flex-wrap gap-2">
                  {CITIES.map((c) => (
                    <Pill key={c} active={f.city_region === c} onClick={() => set("city_region", c)} testid={`pf-city-${c}`}>{c}</Pill>
                  ))}
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-[1fr_160px]">
                <div className="space-y-2">
                  <Label className="text-slate-300">Street Address</Label>
                  <Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St" className={field} data-testid="pf-address" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">ZIP</Label>
                  <Input value={f.zip} onChange={(e) => set("zip", e.target.value)} placeholder="78701" inputMode="numeric" maxLength={10} className={field} data-testid="pf-zip" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Partner type *</Label>
                <div className="flex flex-wrap gap-2">
                  {PARTNER_TYPES.map((t) => (
                    <Pill key={t} active={f.partner_type === t} onClick={() => set("partner_type", t)} testid={`pf-type-${t}`}>{t}</Pill>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Online handle / URL</Label>
                  <Input value={f.online_handle_url} onChange={(e) => set("online_handle_url", e.target.value)} placeholder="instagram.com/…" className={field} data-testid="pf-url" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Audience reach *</Label>
                  <div className="flex flex-wrap gap-2">
                    {REACH.map((r) => (
                      <Pill key={r} active={f.influence_reach === r} onClick={() => set("influence_reach", r)} testid={`pf-reach-${r}`}>{r}</Pill>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Partnership interests</Label>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((i) => (
                    <Pill key={i} active={f.partnership_interests.includes(i)} onClick={() => toggleInterest(i)} testid={`pf-interest-${i}`}>{i}</Pill>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Your pitch / vision</Label>
                <Textarea value={f.pitch_vision} onChange={(e) => set("pitch_vision", e.target.value)} rows={4} placeholder="How would you grow Sparkd in your community?" className={field} data-testid="pf-pitch" />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Payout preference</Label>
                <div className="flex flex-wrap gap-2">
                  {PAYOUTS.map((p) => (
                    <Pill key={p} active={f.payout_preference === p} onClick={() => set("payout_preference", p)} testid={`pf-payout-${p}`}>{p}</Pill>
                  ))}
                </div>
                {f.payout_preference && (
                  <Input
                    value={f.custom_payout_details}
                    onChange={(e) => set("custom_payout_details", e.target.value)}
                    placeholder={PAYOUT_PLACEHOLDERS[f.payout_preference] || "Enter payout details"}
                    className={`${field} mt-2`}
                    data-testid="pf-payout-details"
                  />
                )}
              </div>

              <Button type="submit" disabled={loading} data-testid="partner-submit" className="w-full rounded-xl py-6 text-base font-semibold text-white" style={{ background: `linear-gradient(135deg, ${EMBER}, ${MAGENTA})` }}>
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : <>Apply to Partner <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <Sparkles className="w-3.5 h-3.5" /> Get your referral link &amp; QR instantly
              </p>
            </form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
